import type { DefaultSection, SectionEntity, SpecificSection, ZeroSection } from './schemas';
import { createEntityMeta } from '../utils';

export type SectionVariant = 'default' | 'specific' | 'zero';

/**
 * Host-only value the package deliberately doesn't own: the transparent colour
 * literal lives in the colour-picker presets (`TRANSPARENT_VALUE`), so the host
 * may pass its own rather than have the package hard-code a duplicate.
 */
export type CreateSectionOptions = {
    /** `TRANSPARENT_VALUE` — seed background of a specific section. */
    transparentColor?: string;
};

const DEFAULT_TRANSPARENT_COLOR = 'rgba(255, 255, 255, 0)';

/** `id` and `parentId` are left blank: the host fills them in when it places the row. */
const baseDefaults = {
    id: '',
    parentId: '',
    entityType: 'section' as const,
    children: [] as unknown[],
    version: 0 as const,
};

const px = (value: number) => ({ value, unit: 'px' as const });

export const createDefaultSection = (): DefaultSection => ({
    ...baseDefaults,
    meta: createEntityMeta(),
    entitySubType: 'default',
    props: {
        columnGap: px(0),
        isFlexibleWidths: false,
    },
});

export const createZeroSection = (): ZeroSection => ({
    ...baseDefaults,
    meta: createEntityMeta(),
    entitySubType: 'zero',
    props: {},
});

export const createSpecificSection = (options?: CreateSectionOptions): SpecificSection => ({
    ...baseDefaults,
    meta: createEntityMeta(),
    entitySubType: 'specific',
    props: {
        slotVerticalMargin: px(0),
        columnGap: px(30),
        padding: { value: { top: 15, right: 15, bottom: 15, left: 15 }, unit: 'px' },
        verticalPosition: 'middle',
        slots: { left: true, center: true, right: true },
        background: {
            color: options?.transparentColor ?? DEFAULT_TRANSPARENT_COLOR,
            image: null,
            size: null,
            position: null,
            repeat: null,
        },
    },
});

export function createSection(variant: 'default', options?: CreateSectionOptions): DefaultSection;
export function createSection(variant: 'specific', options?: CreateSectionOptions): SpecificSection;
export function createSection(variant: 'zero', options?: CreateSectionOptions): ZeroSection;
export function createSection(variant?: SectionVariant, options?: CreateSectionOptions): SectionEntity;
export function createSection(variant: SectionVariant = 'default', options?: CreateSectionOptions): SectionEntity {
    if (variant === 'specific') return createSpecificSection(options);
    if (variant === 'zero') return createZeroSection();

    return createDefaultSection();
}
